// Script outline — turn a multi-statement SQL buffer into a navigable list of
// { label, start, end } items (offsets into the source, used to jump the editor
// caret). Pure; `labelFor` is unit-tested. Reuses the quote/comment/DELIMITER-
// aware splitter so a routine body (DELIMITER $$ … $$) is ONE item, not split
// on its internal semicolons.
import { splitScript } from './sqlsplit.js';

// Strip leading whitespace + line/block comments so the first real keyword is
// what we classify on.
const LEAD = /^(\s|--[^\n]*\n?|#[^\n]*\n?|\/\*[\s\S]*?\*\/)+/;

// labelFor produces a short outline label for one statement's text, e.g.
// "SELECT … tuser", "UPDATE dbx.acct", "CREATE PROCEDURE foo". Returns null for
// an empty/comment-only statement (no outline entry).
export function labelFor(text) {
  const t = String(text || '').replace(LEAD, '').trim();
  if (!t) return null;
  const first = (t.match(/^[A-Za-z]+/) || [''])[0].toUpperCase();
  const grab = (re) => {
    const m = t.match(re);
    return m ? m[1].replace(/`/g, '') : null;
  };
  switch (first) {
    case 'SELECT': { const f = grab(/\bFROM\s+([`\w.]+)/i); return f ? `SELECT … ${f}` : 'SELECT'; }
    case 'INSERT': { const f = grab(/\bINTO\s+([`\w.]+)/i); return f ? `INSERT ${f}` : 'INSERT'; }
    case 'REPLACE': { const f = grab(/\bINTO\s+([`\w.]+)/i); return f ? `REPLACE ${f}` : 'REPLACE'; }
    case 'UPDATE': { const f = grab(/^UPDATE\s+([`\w.]+)/i); return f ? `UPDATE ${f}` : 'UPDATE'; }
    case 'DELETE': { const f = grab(/\bFROM\s+([`\w.]+)/i); return f ? `DELETE ${f}` : 'DELETE'; }
    case 'CALL': { const f = grab(/^CALL\s+([`\w.]+)/i); return f ? `CALL ${f}` : 'CALL'; }
    case 'CREATE':
    case 'ALTER':
    case 'DROP': {
      const m = t.match(/^(CREATE|ALTER|DROP)\s+(?:OR\s+REPLACE\s+)?(?:DEFINER\s*=\s*\S+\s+)?(?:TEMPORARY\s+)?(TABLE|VIEW|PROCEDURE|FUNCTION|TRIGGER|EVENT|INDEX|DATABASE|SCHEMA)\s+(?:IF\s+(?:NOT\s+)?EXISTS\s+)?([`\w.]+)/i);
      if (m) return `${m[1].toUpperCase()} ${m[2].toUpperCase()} ${m[3].replace(/`/g, '')}`;
      return first;
    }
    case 'WITH': return 'WITH (CTE)';
    case 'SET': { const v = t.slice(3).trim().split(/\s|=/)[0]; return v ? `SET ${v}`.slice(0, 28) : 'SET'; }
    default: {
      const one = t.replace(/\s+/g, ' ');
      return one.length > 40 ? one.slice(0, 40) + '…' : one;
    }
  }
}

// outlineItems splits the buffer and labels each statement; comment-only /
// empty segments are dropped (labelFor → null).
export function outlineItems(sql) {
  const items = [];
  for (const s of splitScript(sql || '')) {
    const label = labelFor(s.text);
    if (label) items.push({ label, start: s.start, end: s.end });
  }
  return items;
}
